import axios from "axios";

const API = process.env.REACT_APP_API_URL ;

export interface ReportData {
  id?: string;
  caseId: string;
  patientId: string; // This is required but can be an empty string to represent "no patient"
  markdownContent: string;
  status?: string;
  isConfirmed?: boolean;
  createdAt?: string;
  userId?: string;
  versionNumber?: number; // Version number from CaseVersion
}

export interface VersionOut {
  id: string;
  caseId: string;
  versionNumber: number;
  reportId: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  extractionStatus: string;
  activatedAt?: string;
  activatedBy?: string;
  changeDescription?: string;
  isInitial?: boolean;
}

export interface DeletePreviewVersion {
  versionNumber: number;
  caseId: string;
  isActive: boolean;
  extractionCount: number;
  createdAt?: string;
  changeDescription?: string;
}

export interface DeletePreviewData {
  reportId: string;
  reportTitle: string;
  versions: DeletePreviewVersion[];
  totalExtractions: number;
  willDeleteVersions: number;
  warningMessage?: string | null;
}

// Get reports for a specific case with pagination
export async function getReportsByCase(
  token: string,
  caseId: string,
  limit = 25,
  offset = 0
) {
  const res = await axios.get(`${API}/reporting/case/${caseId}`, {
    params: { limit, offset },
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data as ReportData[];
}

// Get a specific report by ID
export async function getReportById(token: string, reportId: string) {
  const res = await axios.get(`${API}/reporting/${reportId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data as ReportData;
}

// Create a new report
export async function createReport(token: string, reportData: ReportData) {
  const res = await axios.post(`${API}/reporting/`, reportData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data as ReportData;
}

// Update an existing report
export async function updateReport(
  token: string,
  reportId: string,
  reportData: ReportData
) {
  const res = await axios.put(`${API}/reporting/${reportId}`, reportData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data as ReportData;
}

// Preview what will be deleted before deleting a report
export async function previewDeleteReport(token: string, reportId: string) {
  const res = await axios.get(`${API}/reporting/${reportId}/preview-delete`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data as DeletePreviewData;
}

// Delete a report
export async function deleteReport(token: string, reportId: string) {
  const res = await axios.delete(`${API}/reporting/${reportId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

// Translate text
export async function translateText(
  token: string,
  text: string,
  target_language = "el"
) {
  const res = await axios.post(
    `${API}/reporting/translate`,
    { text, target_language },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return res.data as { translatedText: string };
}

// Get all reports for the current user with pagination and optional search
export async function getUserReports(
  token: string,
  search?: string,
  limit = 25,
  offset = 0
) {
  const params: Record<string, string | number> = { limit, offset };
  if (search) params.search = search;

  try {
    const res = await axios.get(`${API}/reporting/`, {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });

    console.log("User reports response:", res.data);
    return res.data as ReportData[];
  } catch (error) {
    console.error("Error fetching user reports:", error);
    throw error;
  }
}

// Version Control APIs

// List all versions for a case
export async function listCaseVersions(token: string, caseId: string) {
  const res = await axios.get(`${API}/reporting/versions/${caseId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data as VersionOut[];
}

// Activate a specific version
export async function activateVersion(token: string, versionId: string) {
  const res = await axios.patch(
    `${API}/reporting/versions/${versionId}/activate`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return res.data;
}

// Get the currently active version for a case
export async function getActiveVersion(token: string, caseId: string) {
  const res = await axios.get(`${API}/reporting/versions/active/${caseId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data as VersionOut;
}
